import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  RefreshControl,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { CatalogueCard, CatalogueCardProps } from './CatalogueCard';
import { colors, spacing, typography } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface CatalogueListProps {
  catalogues: CatalogueCardProps['catalogue'][];
  loading?: boolean;
  refreshing?: boolean;
  onRefresh?: () => void;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onCataloguePress?: (catalogueId: string) => void;
  onCatalogueEdit?: (catalogueId: string) => void;
  onCatalogueDelete?: (catalogueId: string) => void;
  onCatalogueManageChannels?: (catalogueId: string) => void;
  showActions?: boolean;
  emptyMessage?: string;
  containerStyle?: any;
  selectionMode?: boolean;
  selectedIds?: string[];
  onToggleSelect?: (catalogueId: string) => void;
}

export const CatalogueList: React.FC<CatalogueListProps> = ({
  catalogues,
  loading = false,
  refreshing = false,
  onRefresh,
  onLoadMore,
  hasMore = false,
  onCataloguePress,
  onCatalogueEdit,
  onCatalogueDelete,
  onCatalogueManageChannels,
  showActions = true,
  emptyMessage = 'No catalogues yet. Create your first catalogue to get started!',
  containerStyle,
  selectionMode = false,
  selectedIds,
  onToggleSelect,
}) => {
  const styles = useThemedStyles(getStyles);
  const renderCatalogue = ({ item }: { item: CatalogueCardProps['catalogue'] }) => (
    <CatalogueCard
      catalogue={item}
      onPress={onCataloguePress}
      onEdit={onCatalogueEdit}
      onDelete={onCatalogueDelete}
      onManageChannels={onCatalogueManageChannels}
      showActions={showActions}
      selectionMode={selectionMode}
      isSelected={!!item._id && !!selectedIds?.includes(item._id)}
      onToggleSelect={onToggleSelect}
    />
  );

  const renderEmpty = () => {
    if (loading) {
      return (
        <View style={styles.emptyContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.emptyText}>Loading catalogues...</Text>
        </View>
      );
    }

    return (
      <View style={styles.emptyContainer}>
        <Text style={styles.emptyText}>{emptyMessage}</Text>
      </View>
    );
  };

  const renderFooter = () => {
    if (!hasMore) return null;
    return (
      <View style={styles.footer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  };

  return (
    <View style={[styles.container, containerStyle]}>
      <FlatList
        data={catalogues}
        renderItem={renderCatalogue}
        keyExtractor={(item, index) => item._id || `catalogue-${index}`}
        ListEmptyComponent={renderEmpty}
        ListFooterComponent={renderFooter}
        refreshControl={
          onRefresh ? (
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          ) : undefined
        }
        onEndReached={hasMore && onLoadMore ? onLoadMore : undefined}
        onEndReachedThreshold={0.5}
        contentContainerStyle={[
          catalogues.length === 0 ? styles.emptyListContent : styles.listContent,
        ]}
        showsVerticalScrollIndicator={false}
      />
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
  },
  listContent: {
    padding: spacing.md,
    paddingBottom: spacing.xl,
  },
  emptyListContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing.xl * 2,
  },
  emptyText: {
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    textAlign: 'center',
    fontFamily: typography.fontFamily.primary,
    marginTop: spacing.md,
  },
  footer: {
    paddingVertical: spacing.md,
    alignItems: 'center',
  },
});

